import React, { useCallback, useEffect, useState } from 'react'
import { NavLink, useHistory } from 'react-router-dom'
import { superAxiosRequest } from 'axiosApi'
import { ExcelRowsParams } from 'components/utils/ExcelExport'
import useTableFixedHeader from 'hooks/useTableFixedHeader'
import ContentHeader from 'components/UI/ContentHeader/ContentHeader'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import THead from 'components/ContentBox/Global/ReportTable/THead'
import styles from './FormedGroups.module.scss'
import ExportExcelButton from '../ExportExcelButton/ExportExcelButton'
import { observer } from 'mobx-react'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import useLocalization from 'hooks/useLocalization'
import { timeConverter } from 'components/ContentBox/Online-schedule/components/functions'

const apiConf = {
  method: 'get',
  link: 'progress_report/formed_groups_report',
}

const cols = [
  { name: 'ФИО ученика' },
  { name: 'Письменный тест' },
  { name: 'Интервал' },
  { name: 'Устный тест' },
  { name: 'Интервал' },
  { name: 'Формирование группы' },
  { name: 'В днях в ожидании группы' },
]

const getDaysAwaitingGroup = (lesson_start_date, group_formed_date) =>
  Math.floor(
    (new Date(lesson_start_date || Date.now()) - new Date(group_formed_date)) /
      (24 * 60 * 60 * 1000),
  )

const getTotalDaysAwaitingForming = (arr) =>
  arr?.reduce(
    (acc, el) =>
      acc + getDaysAwaitingGroup(el.lesson_start_date, el.group_formed_date),
    0,
  ) || 0

const calculateTotalInterval = (data, key) => {
  return data.reduce((sum, item) => sum + (item[key] || 0), 0)
}

const downloadExcel = (data) => {
  import('xlsx-js-style').then((XLSX) => {
    const headRows = [
      cols.map(({ name }) => ({
        v: name,
        ...ExcelRowsParams.headerRowParam,
      })),
    ]

    const wscols = [40, 30, 30, 30, 30, 30, 30].map((wch) => ({ wch }))

    const tableData = []

    data.forEach((client) => {
      tableData.push(
        [
          client.name,
          client.written_test_date || '-',
          client.written_verbal_interval || '-',
          client.verbal_test_date || '-',
          client.verbal_first_lesson_interval || '-',
          client.group_formed_date,
          getDaysAwaitingGroup(
            client.lesson_start_date,
            client.group_formed_date,
          ),
        ].map((v) => ({
          v,
          ...ExcelRowsParams.tableRowParam.cell,
        })),
      )
    })

    // Итого
    const totalDaysAwaitingForming = getTotalDaysAwaitingForming(data)
    const totalWrittenVerbalInterval = calculateTotalInterval(
      data,
      'written_verbal_interval',
    )
    const totalVerbalFirstLessonInterval = calculateTotalInterval(
      data,
      'verbal_first_lesson_interval',
    )
    const emptyTotalCell = {
      v: '',
      ...ExcelRowsParams.footerRowParams.cell,
    }

    tableData.unshift([
      {
        v: `Итого`,
        ...ExcelRowsParams.footerRowParams.firstCell,
      },
      emptyTotalCell,
      {
        v: `Всего ${totalWrittenVerbalInterval}, Среднее ${Math.floor(totalWrittenVerbalInterval / data.length)}`,
        ...ExcelRowsParams.footerRowParams.cell,
      },
      emptyTotalCell,
      {
        v: `Всего ${totalVerbalFirstLessonInterval}, Среднее ${Math.floor(totalVerbalFirstLessonInterval / data.length)}`,
        ...ExcelRowsParams.footerRowParams.cell,
      },
      emptyTotalCell,
      {
        v: `Всего ${totalDaysAwaitingForming}, Среднее ${Math.floor(totalDaysAwaitingForming / data.length)}`,
        ...ExcelRowsParams.footerRowParams.cell,
      },
    ])

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet([...headRows, ...tableData])
    ws['!cols'] = wscols

    XLSX.utils.book_append_sheet(wb, ws, 'Формирование групп')
    XLSX.writeFile(wb, 'Формирование групп.xlsx')
  })
}

const FormedGroups = () => {
  const {
    refWrapper,
    refSticky,
    refThead,
    data,
    setData,
    isLoading,
    setIsLoading,
  } = useTableFixedHeader()
  const dateAfter = new Date()
  dateAfter.setMonth(dateAfter.getMonth() - 1)
  const firstDayOfLastMoth = new Date(new Date(dateAfter).setDate(1))
  const lastDayOfLastMoth = new Date(new Date().setDate(0))

  const { translate } = useLocalization({
    section: 'ebooks',
    subsection: 'ebooks_books',
    dict: {
      edit: 'Edit',
      copy: 'Copy',
      delete: 'Delete',
      save: 'Save',
      filter: 'Filter',
      company: 'Company',
      reset_all: 'Reset all',
      apply: 'Apply',
      search: 'Search',
      start_date: 'Start date',
      end_date: 'End date',
    },
  })

  const [catalog, setCatalog] = useState({})
  const [filter, updateFilter] = useLocalStorageFilter()
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore
  const history = useHistory()

  const filterCnf = [
    {
      type: 'select-multi',
      label: 'Локация',
      placeholder: 'Локация',
      values: catalog?.locations,
      sort_key: 'name',
      nameState: 'location',
    },
    {
      type: 'switcher',
      label: 'Показать письменный тест',
      name: 'written_test_date',
    },
    {
      type: 'switcher',
      label: 'Показать устный тест',
      name: 'verbal_test_date',
    },
    {
      type: 'date_day_picker',
      defaultDate: {
        after: firstDayOfLastMoth,
        before: lastDayOfLastMoth,
      },
      nameState: ['date_from', 'date_until'],
      label: 'Период',
      isRequired: true,
    },
  ]

  const calculateTotal = (data, key) => {
    return data.flat().reduce((sum, item) => sum + item[key], 0)
  }

  useEffect(() => {
    if (filter && Object.keys(filter).length > 0) {
      setIsLoading(true)
      superAxiosRequest({
        ...apiConf,
        params: {
          ...filter,
          date_from: timeConverter(
            filter?.date_from ? filter?.date_from : firstDayOfLastMoth,
            'today',
          ),
          date_until: timeConverter(
            filter?.date_until ? filter?.date_until : lastDayOfLastMoth,
            'today',
          ),
        },
      })
        .then(({ data }) => {
          setData(data.report.sort((a, b) => (a.name > b.name ? 1 : -1)))
          setIsLoading(false)
        })
        .catch(() => {
          setIsLoading(false)
        })
    }
  }, [filter])

  const fetchCatalog = useCallback(() => {
    setIsLoading(true)
    superAxiosRequest(apiConf)
      .then(({ data }) => {
        setIsLoading(false)
        setCatalog(data)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [setIsLoading])

  useEffect(() => {
    fetchCatalog()
  }, [fetchCatalog])

  const totalDaysAwaitingForming = getTotalDaysAwaitingForming(data)

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/reports/formed_groups') {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  return (
    <div>
      <ContentHeader title='Формирование групп'>
        <div className={styles.filter}>
          <SearchBooks
            onSearch={(text) => {
              updateFilter({ ...filter, search: text })
              changeTextValue('search', {
                key: 'search',
                label: 'Search',
                value: text,
              })
            }}
            onClear={() => {
              const { search, ...obj } = filter
              updateFilter(obj)
              setTextValues(
                filterTextValues.filter((field) => field.key !== 'search'),
              )
            }}
            translate={translate}
            placeholder={`Search...`}
            value={filter?.search || ''}
          />
          {!!filterTextValues.length && (
            <FilterTextValues
              onDelete={(key) => {
                const { [key]: _, ...obj } = filter
                updateFilter(obj)
              }}
            />
          )}
          {isOpenFilter && (
            <AsideFilter
              translate={translate}
              getFilterParams={updateFilter}
              config={filterCnf}
              filterValue={filter}
              onDropFilter={() => {
                updateFilter({})
                closeFilter()
              }}
            />
          )}
        </div>
      </ContentHeader>

      {isLoading ? (
        <Loader />
      ) : data && data.length > 0 ? (
        <>
          <div className={styles.excel}>
            <ExportExcelButton data={data} downloadExcel={downloadExcel}>
              Выгрузить в Excel
            </ExportExcelButton>
          </div>
          <div className={styles.tableWrapper} ref={refWrapper}>
            <div ref={refSticky} className={styles.sticky}>
              <table className={styles.table}>
                <THead mode='new' cols={cols} />
              </table>
            </div>
            <table className={`${styles.table} ${styles.tbodyStriped}`}>
              <THead mode='new' cols={cols} ref={refThead} />
              <tbody className={styles.tbody}>
                <tr>
                  <td className={`${styles.tdTotal} ${styles.colName}`}>
                    Итого
                  </td>
                  <td className={`${styles.tdTotal} ${styles.colName}`}>-</td>
                  <td
                    className={`${styles.tdTotal} ${styles.colName} ${styles.textAlignLeft} ${styles.colDays}  ${styles.tdTotalDays}`}
                  >
                    <div>
                      Всего {calculateTotal(data, 'written_verbal_interval')},
                      Среднее{' '}
                      {Math.floor(
                        calculateTotal(data, 'written_verbal_interval') /
                          data.length,
                      )}
                    </div>
                  </td>
                  <td className={`${styles.tdTotal} ${styles.colName}`}>-</td>
                  <td
                    className={`${styles.tdTotal} ${styles.colName} ${styles.textAlignLeft} ${styles.colDays}  ${styles.tdTotalDays}`}
                  >
                    <div>
                      Всего{' '}
                      {calculateTotal(data, 'verbal_first_lesson_interval')},
                      Среднее{' '}
                      {Math.floor(
                        calculateTotal(data, 'verbal_first_lesson_interval') /
                          data.length,
                      )}
                    </div>
                  </td>
                  <td className={`${styles.tdTotal} ${styles.colName}`}>-</td>
                  <td
                    className={`${styles.tdTotal} ${styles.tdTotalDays} ${styles.textAlignLeft} ${styles.colDays}`}
                  >
                    <div>
                      Всего {totalDaysAwaitingForming}, Среднее{' '}
                      {Math.floor(totalDaysAwaitingForming / data.length)}
                    </div>
                  </td>
                </tr>
                {Array.isArray(data) &&
                  data.map((row, index) => (
                    <tr key={index}>
                      <td
                        className={`${styles.textAlignLeft} ${styles.colName}`}
                      >
                        <NavLink
                          to={'/account_clients/' + row.user_id}
                          target='_blank'
                        >
                          {row.name}
                        </NavLink>
                      </td>
                      <td
                        className={`${styles.textAlignLeft}  ${styles.colWrittenTest}`}
                      >
                        {row.written_test_date || '-'}
                      </td>
                      <td
                        className={`${styles.textAlignLeft}  ${styles.colWrittenVerbalInterval}`}
                      >
                        {row.written_verbal_interval || '-'}
                      </td>
                      <td
                        className={`${styles.textAlignLeft}  ${styles.colVerbalTest}`}
                      >
                        {row.verbal_test_date || '-'}
                      </td>
                      <td
                        className={`${styles.textAlignLeft}  ${styles.colVerbalFirstLessonInterval}`}
                      >
                        {row.verbal_first_lesson_interval || '-'}
                      </td>
                      <td
                        className={`${styles.textAlignLeft}  ${styles.colGroupFormed}`}
                      >
                        {row.group_formed_date}
                      </td>
                      <td
                        className={`${styles.textAlignLeft}  ${styles.colDays}`}
                      >
                        {getDaysAwaitingGroup(
                          row.lesson_start_date,
                          row.group_formed_date,
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <h1 className={styles.noData}>Нет данных для отображения!</h1>
      )}
    </div>
  )
}

export default observer(FormedGroups)
